/**
 * Inference about correlation coefficients from summary statistics: Fisher's z.
 *
 * Fisher's transformation z = atanh(r) makes r roughly normal with standard error
 * 1/√(n − 3), whatever the true correlation, which supports:
 *
 * - a confidence interval for one r (the interval R's `cor.test` reports) and a test
 *   against a hypothesised ρ₀;
 * - a z test that two **independent** correlations (different samples) are equal, with
 *   Zou's (2007) confidence interval for their difference;
 * - tests that two **dependent, overlapping** correlations (r_jk and r_jh share variable
 *   j and come from the same people) are equal: Steiger's (1980) modification of the
 *   Dunn–Clark z, Williams's (1959) t and Hotelling's (1940) t;
 * - a homogeneity (Q) test for k independent correlations with a pooled estimate.
 *
 * R has no base function for the comparisons, so the unit tests pin the formulas to
 * independent R arithmetic (`atanh`, `pnorm`, `pt`, `pchisq`) and the formulas themselves
 * were checked by Monte Carlo in R (20,000 simulated samples of n = 60 under the null,
 * four correlation structures): Steiger's Z has SD 0.99–1.01 and rejects at 4.7–5.3%.
 */

import {
  chiSquareUpper,
  normalCdf,
  normalQuantile,
  studentTCdf,
} from "@/lib/stats/distributions";

export type Alternative = "two-sided" | "greater" | "less";

/** True for a usable correlation: strictly between −1 and 1. */
export function validR(r: number): boolean {
  return Number.isFinite(r) && Math.abs(r) < 1;
}

export function fisherZ(r: number): number {
  return Math.atanh(r);
}

/** p-value of a z statistic under the chosen alternative. */
export function zP(z: number, alternative: Alternative): number {
  if (alternative === "greater") {
    return 1 - normalCdf(z);
  }
  if (alternative === "less") {
    return normalCdf(z);
  }
  return 2 * (1 - normalCdf(Math.abs(z)));
}

/** p-value of a t statistic under the chosen alternative. */
export function tP(t: number, df: number, alternative: Alternative): number {
  if (alternative === "greater") {
    return 1 - studentTCdf(t, df);
  }
  if (alternative === "less") {
    return studentTCdf(t, df);
  }
  return 2 * (1 - studentTCdf(Math.abs(t), df));
}

export interface Interval {
  low: number;
  high: number;
}

/** Fisher-z confidence interval for ρ given r and n (needs n > 3). */
export function correlationInterval(
  r: number,
  n: number,
  level: number
): Interval | null {
  if (!validR(r) || n <= 3) {
    return null;
  }
  const half = normalQuantile(1 - (1 - level) / 2) / Math.sqrt(n - 3);
  const z = fisherZ(r);
  return { low: Math.tanh(z - half), high: Math.tanh(z + half) };
}

export interface SingleReport {
  r: number;
  n: number;
  z: number;
  standardError: number;
  ci: Interval;
  /** Present when a hypothesised ρ₀ was supplied. */
  test: { rho0: number; z: number; p: number } | null;
}

export function singleCorrelation(
  r: number,
  n: number,
  level: number,
  rho0: number | null,
  alternative: Alternative
): SingleReport | null {
  const ci = correlationInterval(r, n, level);
  if (ci === null) {
    return null;
  }
  const z = fisherZ(r);
  const standardError = 1 / Math.sqrt(n - 3);
  let test: SingleReport["test"] = null;
  if (rho0 !== null && validR(rho0)) {
    const stat = (z - fisherZ(rho0)) / standardError;
    test = { rho0, z: stat, p: zP(stat, alternative) };
  }
  return { r, n, z, standardError, ci, test };
}

export interface IndependentReport {
  z1: number;
  z2: number;
  difference: number;
  standardError: number;
  z: number;
  p: number;
  ci1: Interval;
  ci2: Interval;
  /** Zou's interval for ρ₁ − ρ₂. */
  differenceCi: Interval;
}

export function compareIndependent(
  r1: number,
  n1: number,
  r2: number,
  n2: number,
  level: number,
  alternative: Alternative
): IndependentReport | null {
  const ci1 = correlationInterval(r1, n1, level);
  const ci2 = correlationInterval(r2, n2, level);
  if (ci1 === null || ci2 === null) {
    return null;
  }
  const z1 = fisherZ(r1);
  const z2 = fisherZ(r2);
  const standardError = Math.sqrt(1 / (n1 - 3) + 1 / (n2 - 3));
  const z = (z1 - z2) / standardError;
  const difference = r1 - r2;
  return {
    z1,
    z2,
    difference,
    standardError,
    z,
    p: zP(z, alternative),
    ci1,
    ci2,
    differenceCi: {
      low: difference - Math.sqrt((r1 - ci1.low) ** 2 + (ci2.high - r2) ** 2),
      high: difference + Math.sqrt((ci1.high - r1) ** 2 + (r2 - ci2.low) ** 2),
    },
  };
}

export interface DependentReport {
  /** The determinant of the 3 × 3 correlation matrix. */
  determinant: number;
  steiger: { z: number; p: number; covariance: number };
  williams: { t: number; p: number };
  hotelling: { t: number; p: number };
  df: number;
  difference: number;
}

/**
 * Compare r_jk with r_jh, which share variable j, measured on the same n cases, given
 * the correlation r_kh between the two other variables.
 */
export function compareDependent(
  rjk: number,
  rjh: number,
  rkh: number,
  n: number,
  alternative: Alternative
): DependentReport | null {
  if (![rjk, rjh, rkh].every(validR) || n <= 3) {
    return null;
  }
  const determinant =
    1 - rjk * rjk - rjh * rjh - rkh * rkh + 2 * rjk * rjh * rkh;
  // A correlation matrix must be positive definite.
  if (determinant <= 0) {
    return null;
  }
  const mean = (rjk + rjh) / 2;
  const psi =
    rkh * (1 - 2 * mean * mean) -
    0.5 * mean * mean * (1 - 2 * mean * mean - rkh * rkh);
  const covariance = psi / (1 - mean * mean) ** 2;
  const steigerZ =
    ((fisherZ(rjk) - fisherZ(rjh)) * Math.sqrt(n - 3)) /
    Math.sqrt(2 - 2 * covariance);
  const difference = rjk - rjh;
  const df = n - 3;
  const hotellingT =
    difference * Math.sqrt(((n - 3) * (1 + rkh)) / (2 * determinant));
  const williamsT =
    difference *
    Math.sqrt(
      ((n - 1) * (1 + rkh)) /
        (2 * ((n - 1) / (n - 3)) * determinant + mean * mean * (1 - rkh) ** 3)
    );
  return {
    determinant,
    steiger: { z: steigerZ, p: zP(steigerZ, alternative), covariance },
    williams: { t: williamsT, p: tP(williamsT, df, alternative) },
    hotelling: { t: hotellingT, p: tP(hotellingT, df, alternative) },
    df,
    difference,
  };
}

export interface HeterogeneityReport {
  k: number;
  q: number;
  df: number;
  p: number;
  /** I² = max(0, (Q − df) / Q): the share of variation beyond chance. */
  i2: number;
  pooled: number;
  pooledCi: Interval;
  /** Each study's weight n − 3, as a share of the total. */
  weights: number[];
}

/** Homogeneity of k ≥ 2 independent correlations, with the fixed-effect pooled r. */
export function compareSeveral(
  rs: readonly number[],
  ns: readonly number[],
  level: number
): HeterogeneityReport | null {
  const k = rs.length;
  if (k < 2 || ns.length !== k || !rs.every(validR) || ns.some((n) => n <= 3)) {
    return null;
  }
  const w = ns.map((n) => n - 3);
  const total = w.reduce((a, b) => a + b, 0);
  const zs = rs.map(fisherZ);
  const zBar = zs.reduce((sum, z, i) => sum + w[i] * z, 0) / total;
  const q = zs.reduce((sum, z, i) => sum + w[i] * (z - zBar) ** 2, 0);
  const df = k - 1;
  const half = normalQuantile(1 - (1 - level) / 2) / Math.sqrt(total);
  return {
    k,
    q,
    df,
    p: chiSquareUpper(q, df),
    i2: q > 0 ? Math.max(0, (q - df) / q) : 0,
    pooled: Math.tanh(zBar),
    pooledCi: { low: Math.tanh(zBar - half), high: Math.tanh(zBar + half) },
    weights: w.map((value) => value / total),
  };
}
